// src/pages/msme/SigmaStudioPokayoke.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faTimes,
  faExclamationTriangle,
  faCalendarAlt,
  faExclamationCircle,
  faCheckCircle,
  faBarcode,
} from "@fortawesome/free-solid-svg-icons";

const today = new Date().toISOString().split("T")[0];

const initialSampleData = [
  {
    id: 1,
    productName: "Premium Hair Oil 100ml",
    batchNumber: "HO-B24001",
    manufacturingDate: "2025-09-05",
    expiryDate: "2026-09-04",
    labelType: "Primary",
    printerUsed: "Zebra GK420t",
    status: "approved",           // pending / approved / rejected
    notes: "Batch matches artwork v3.2",
  },
  {
    id: 2,
    productName: "Neem Face Wash 150g",
    batchNumber: "FW-N24012",
    manufacturingDate: "2025-09-10",
    expiryDate: "2027-03-09",
    labelType: "Secondary (carton)",
    printerUsed: "TSC TE200",
    status: "pending",
    notes: "Waiting for final color approval",
  },
  {
    id: 3,
    productName: "Aloe Vera Gel 200ml",
    batchNumber: "AV-G24005",
    manufacturingDate: "2025-08-20",
    expiryDate: "2025-08-19",     // expired example
    labelType: "Primary",
    printerUsed: "Zebra GK420t",
    status: "rejected",
    notes: "Wrong font size on ingredients list",
  },
];

const statusLabels = {
  pending:   { label: "Pending",   color: "bg-amber-600",   icon: faExclamationCircle },
  approved:  { label: "Approved",  color: "bg-emerald-600", icon: faCheckCircle },
  rejected:  { label: "Rejected",  color: "bg-red-600",     icon: faExclamationTriangle },
};

const inputClass = "px-4 py-3 rounded-lg bg-gray-800 text-gray-100 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm";

const buttonClass = "px-5 py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 min-h-[48px]";

const SigmaStudioPokayoke = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [records, setRecords] = useState(initialSampleData);
  const [formData, setFormData] = useState({
    productName: "",
    batchNumber: "",
    manufacturingDate: "",
    expiryDate: "",
    labelType: "Primary",
    printerUsed: "",
    status: "pending",
    notes: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState(null);

  const resetForm = () => {
    setFormData({
      productName: "",
      batchNumber: "",
      manufacturingDate: "",
      expiryDate: "",
      labelType: "Primary",
      printerUsed: "",
      status: "pending",
      notes: "",
    });
    setEditingId(null);
    setShowForm(false);
  };

  const showNotif = (msg, type = "info") => {
    setNotification({ id: Date.now(), msg, type });
    setTimeout(() => setNotification(null), 4800);
  };

  const isDuplicate = (newRecord, excludeId = null) => {
    return records.some(r =>
      r.id !== excludeId &&
      r.productName === newRecord.productName &&
      r.batchNumber === newRecord.batchNumber &&
      r.manufacturingDate === newRecord.manufacturingDate
    );
  };

  const isExpiringSoon = (exp) => {
    if (!exp) return false;
    const expDate = new Date(exp);
    const now = new Date();
    const diffDays = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 90;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.productName.trim() || !formData.batchNumber.trim() ||
        !formData.manufacturingDate || !formData.expiryDate || !formData.printerUsed.trim()) {
      showNotif("Please fill all required fields.", "error");
      return;
    }

    const newRecord = { ...formData, id: editingId || Date.now() };

    if (isDuplicate(newRecord, editingId)) {
      showNotif("This product + batch + mfg date combination already exists!", "error");
      return;
    }

    if (new Date(formData.expiryDate) <= new Date(formData.manufacturingDate)) {
      showNotif("Expiry date must be after manufacturing date.", "error");
      return;
    }

    if (editingId) {
      setRecords(records.map(r => r.id === editingId ? newRecord : r));
      showNotif("Label record updated.", "success");
    } else {
      setRecords([...records, newRecord]);
      showNotif("New label record added.", "success");
    }

    resetForm();
  };

  const handleEdit = (record) => {
    setFormData(record);
    setEditingId(record.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this label record?")) return;
    setRecords(records.filter(r => r.id !== id));
    showNotif("Record deleted.", "warning");
    if (editingId === id) resetForm();
  };

  const handleRowClick = (record) => {
    handleEdit(record);
  };

  // Add state for tracking which example we're showing
  const [exampleIndex, setExampleIndex] = useState(0);

  const populateFromExample = () => {
    if (initialSampleData.length === 0) return;

    const example = initialSampleData[exampleIndex];
    
    setFormData({
      ...example,
      id: undefined, // just in case
      notes: example.notes + "\n\n(Example #" + (exampleIndex + 1) + " - please modify)",
    });

    setEditingId(null);
    setShowForm(true);
    showNotif(`Loaded example #${exampleIndex + 1} of ${initialSampleData.length}`, "info");

    // Move to next example for next click
    setExampleIndex((prev) => (prev + 1) % initialSampleData.length);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            Poka-Yoke - The Mistakeproofing Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-8 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700" title="Logout">
                ⏻
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm">
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(s => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-sm">{userName}</div>
              <button onClick={handleLogout} className="p-2 rounded-md bg-red-600">⏻</button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">🔐 Login</Link>
            </div>
          )}
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-xl shadow-2xl z-50 text-white max-w-sm flex items-center gap-3 ${
            notification.type === "success" ? "bg-emerald-600" :
            notification.type === "error"   ? "bg-red-600" :
            notification.type === "warning" ? "bg-amber-600" : "bg-indigo-600"
          }`}
        >
          <span className="flex-1">{notification.msg}</span>
          <button onClick={() => setNotification(null)} className="text-xl">×</button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mt-4 mb-4">
        <button
            onClick={populateFromExample}
            className={`${buttonClass} w-64 bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            <FontAwesomeIcon icon={faPlus} /> Populate From Examle Record
          </button>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className={`${buttonClass} w-64 bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            <FontAwesomeIcon icon={faPlus} /> Add New Record
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-800 p-6 rounded-xl mb-8 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl text-yellow-200 font-semibold">
              {editingId ? "Edit Record" : "New Record"}
            </h2>
            <button type="button" onClick={resetForm} className="w-10 p-2 text-gray-400 bg-blue-700 hover:text-gray-200 text-xl">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Product Name *</label>
              <input
                required
                value={formData.productName}
                onChange={e => setFormData({ ...formData, productName: e.target.value })}
                className={inputClass}
                placeholder="e.g. Premium Hair Oil 100ml"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Batch Number *</label>
              <input
                required
                value={formData.batchNumber}
                onChange={e => setFormData({ ...formData, batchNumber: e.target.value.toUpperCase() })}
                className={inputClass}
                placeholder="e.g. HO-B24001"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Manufacturing Date *</label>
              <input
                type="date"
                required
                value={formData.manufacturingDate}
                max={today}
                onChange={e => setFormData({ ...formData, manufacturingDate: e.target.value })}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Expiry Date *</label>
              <input
                type="date"
                required
                value={formData.expiryDate}
                min={formData.manufacturingDate || today}
                onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Type</label>
              <select
                value={formData.labelType}
                onChange={e => setFormData({ ...formData, labelType: e.target.value })}
                className={inputClass}
              >
                <option>Primary</option>
                <option>Secondary (carton)</option>
                <option>Batch Sticker</option>
                <option>Warning / Allergen</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Printer Used *</label>
              <input
                required
                value={formData.printerUsed}
                onChange={e => setFormData({ ...formData, printerUsed: e.target.value })}
                className={inputClass}
                placeholder="e.g. Zebra GK420t"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-sm text-gray-400 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value })}
              className={inputClass}
            >
              {Object.keys(statusLabels).map(key => (
                <option key={key} value={key}>{statusLabels[key].label}</option>
              ))}
            </select>
          </div>

          <div className="mt-5">
            <label className="block text-sm text-gray-400 mb-1">Notes / Poka-Yoke Check</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              style={{height :"150px", width: "100%"}}
              className={`${inputClass} resize-none`}
              placeholder="e.g. Color verified, font size correct, barcode scannable..."
            />
          </div>

          <div className="mt-2 flex flex-row gap-4">
            <button type="submit" className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white w-32 flex-1`}>
              {editingId ? "Update Record" : "Save New Record"}
            </button>
            <button type="button" onClick={resetForm} className={`${buttonClass} w-24 bg-gray-700 hover:bg-gray-600 text-white flex-1`}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* List / Table */}
      <div className="bg-gray-800 rounded-xl shadow-xl overflow-hidden">
        <div className="p-5 border-b border-gray-700">
          <h3 className="text-lg text-white font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faBarcode} className="text-white-400" />
            Label Printing & Approval Records
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Click any row to edit • Poka-Yoke validations prevent duplicates & date errors
          </p>
        </div>

        {records.length === 0 ? (
          <div className="p-12 text-center text-gray-500 italic">
            No records yet. Add your first label entry.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left">Batch</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Mfg / Exp</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Type / Printer</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map(rec => {
                  const expiring = isExpiringSoon(rec.expiryDate);
                  const expired   = new Date(rec.expiryDate) < new Date();
                  return (
                    <tr
                      key={rec.id}
                      onClick={() => handleRowClick(rec)}
                      className="border-t border-gray-700 hover:bg-gray-700/70 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium">{rec.productName}</td>
                      <td className="px-4 py-3">{rec.batchNumber}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex flex-col text-xs">
                          <span>Mfg: {rec.manufacturingDate}</span>
                          <span className={expired ? "text-red-400" : expiring ? "text-amber-400" : ""}>
                            Exp: {rec.expiryDate}
                            {expired && " (Expired)"}
                            {expiring && !expired && " (Soon)"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-xs">
                        {rec.labelType}<br/>
                        <span className="text-gray-500">{rec.printerUsed}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusLabels[rec.status].color}`}>
                          <FontAwesomeIcon icon={statusLabels[rec.status].icon} className="w-3 h-3" />
                          {statusLabels[rec.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleEdit(rec)}
                            className="p-2 text-amber-400 hover:text-amber-300"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDelete(rec.id)}
                            className="p-2 text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioPokayoke;